import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { uniqBy } from "lodash";
import { Ban, CircleCheck, CircleX, Eye, Pencil, Trash2, UserPlus } from "lucide-react";
import { Badge } from "components/ui/badge";
import Button from "components/shared/Button";
import Avatar from "components/shared/Avatar";
import Select from "components/shared/inputs/Select";
import ResourceSelect from "components/shared/inputs/ResourceSelect";
import StatusCell from "../components/StatusCell";
import { useCompanyPreferences } from "../../../../Settings/Configuration/hooks/usePreferences";
import { useQualificationPreferences, type QualificationField } from "../../../Component/hooks/useQualificationPreferences";
import { useUser } from "utils/hooks/user";
import { useCompanyCurrency, useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { renderSettingsResourceLabel, getSettingsResourceSearchFilters } from "utils/helpers/settings";
import { renderUserName } from "utils/helpers/user";
import { renderCurrencySymbol } from "utils/helpers/job";
import { cn } from "lib/utils";
import type { Tab } from "../components/Menu";

const columnHelper = createColumnHelper<any>();

export const STATUS_CLASSNAMES: Record<string, string> = {
    pending: "tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200",
    approved: "tw-bg-green-50 tw-text-green-700 tw-border-green-200",
    denied: "tw-bg-red-50 tw-text-red-700 tw-border-red-200",
    canceled: "tw-bg-gray-50 tw-text-gray-600 tw-border-gray-200",
    expired: "tw-bg-gray-50 tw-text-gray-600 tw-border-gray-200",
};

const TYPE_CLASSNAMES: Record<string, string> = {
    "open-shift": "tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200",
    "call-off": "tw-bg-orange-50 tw-text-orange-700 tw-border-orange-200",
    "direct-offer": "tw-bg-violet-50 tw-text-violet-700 tw-border-violet-200",
};

const getSchedule = (request: any) => request?.data?.schedule;
const getSection = (request: any) => getSchedule(request)?.sections?.[0];

const renderResourceFilter = (resourcePath: string) => (
    <ResourceSelect
        labelPropName="description"
        resourcePath={resourcePath}
        mode="multiple"
        renderLabel={renderSettingsResourceLabel}
        getSearchFilter={getSettingsResourceSearchFilters}
        showSearch
    />
);

interface UseColumnsProps {
    approve: (request: any) => void;
    deny: (request: any) => void;
    cancel: (request: any) => void;
    edit: (request: any) => void;
    view: (request: any) => void;
    remove: (request: any) => void;
    assign: (request: any) => void;
    openProfile: (user: any) => void;
    menu: Tab;
}

// Qualifications are stored per schedule section; fall back to the request data for older requests
export const getQualifications = (request: any, field: QualificationField): any[] => {
    const sections: any[] = getSchedule(request)?.sections ?? [];
    const items = [...sections.flatMap((section) => section?.[field] ?? []), ...(request?.data?.[field] ?? [])];
    return uniqBy(items.filter(Boolean), (item: any) => item?.id ?? item);
};

const getExtraPayment = (request: any) => request?.data?.extraPayment;

const hasExtraPay = (request: any) => {
    const payAmount = getExtraPayment(request)?.payAmount;
    return payAmount != null && payAmount !== "";
};

const getId = (value: any) => typeof value === "object" ? value?.id : value;

export const isCreatedBy = (request: any, user: any) =>
    !!user?.id && getId(request?.createdBy) === user.id;

const isEditable = (request: any) =>
    request?.status === "pending" || (request?.status === "approved" && !request?.data?.takeUser);

const getStartDate = (request: any) => getSchedule(request)?.startDate || request?.data?.startDate;

const formatTimeLeft = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return hours ? `${hours}h${rest ? ` ${rest}m` : ""}` : `${rest}m`;
};

export const useColumns = ({ approve, deny, cancel, edit, view, remove, assign, openProfile, menu }: UseColumnsProps) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const currency = useCompanyCurrency();
    const user = useUser();
    const coverageDeadline = useCompanyPreferences("requests.openShift.coverageDeadline") ?? 3;
    const qualifications = useQualificationPreferences();

    return useMemo(() => [
        columnHelper.accessor((request) => request?.data?.originalUser || request?.user, {
            id: "user",
            header: t("name"),
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.getValue();
                return value ? (
                    <button
                        type="button"
                        className="tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-0 tw-p-0 tw-text-left hover:tw-text-primary"
                        onClick={() => openProfile(value)}
                    >
                        <Avatar user={value} size="sm" />
                        {renderUserName(value)}
                    </button>
                ) : "—";
            },
            filterType: "text",
        }),
        columnHelper.accessor("type", {
            header: t("type"),
            cell: (info) => {
                const value = info.getValue();
                return value ? (
                    <Badge variant="outline" className={cn("tw-whitespace-nowrap", TYPE_CLASSNAMES[value])}>
                        {t(value)}
                    </Badge>
                ) : "—";
            },
        }),
        columnHelper.accessor((request) => getSchedule(request)?.date || request?.data?.startDate, {
            id: "date",
            header: t("date"),
            cell: (info) => {
                const value = info.getValue();
                return value ? moment.parseZone(value).format(dateFormat) : "—";
            },
        }),
        columnHelper.display({
            id: "time",
            header: t("time"),
            cell: ({ row }) => {
                const schedule = getSchedule(row.original);
                const startDate = schedule?.startDate || row.original?.data?.startDate;
                const endDate = schedule?.endDate || row.original?.data?.endDate;
                if (!startDate || !endDate) return "—";
                return (
                    <Badge variant="outline" className="tw-whitespace-nowrap tw-bg-slate-50 tw-text-slate-700 tw-border-slate-200">
                        {`${moment.parseZone(startDate).format(timeFormat)} - ${moment.parseZone(endDate).format(timeFormat)}`}
                    </Badge>
                );
            },
        }),
        columnHelper.accessor((request) => getSection(request)?.job || request?.data?.job, {
            id: "job",
            header: t("job"),
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.getValue();
                return value ? renderSettingsResourceLabel(value) : "—";
            },
            filterType: "resource-select",
            filter: renderResourceFilter("/jobs"),
        }),
        columnHelper.accessor((request) => getSection(request)?.location || request?.data?.location, {
            id: "location",
            header: t("location"),
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.getValue();
                return value ? renderSettingsResourceLabel(value) : "—";
            },
            filterType: "resource-select",
            filter: renderResourceFilter("/locations"),
        }),
        columnHelper.accessor("data.reason", {
            id: "reason",
            header: t("reason"),
            cell: (info) => {
                const value = info.getValue();
                return value ? (
                    <span className="tw-block tw-max-w-60 tw-truncate" title={value}>{value}</span>
                ) : "—";
            },
        }),
        columnHelper.display({
            id: "extraPay",
            header: t("extra-pay"),
            cell: ({ row }) => {
                if (!hasExtraPay(row.original)) return "—";
                const { payType, payAmount } = getExtraPayment(row.original);
                return (
                    <div className="tw-flex tw-flex-col">
                        <span>{`${renderCurrencySymbol(currency)}${payAmount}`}</span>
                        <span className="tw-text-xs tw-text-muted-foreground">
                            {t(payType === "hourly" ? "per-hour" : "flat-amount")}
                        </span>
                    </div>
                );
            },
        }),
        columnHelper.accessor((row) => getQualifications(row, "skills"), {
            id: "skills",
            header: t("skills"),
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.getValue();
                return value?.length ? value.map(renderSettingsResourceLabel).join(", ") : "—";
            },
            filterType: "resource-select",
            filter: renderResourceFilter("/skill"),
        }),
        columnHelper.accessor((row) => getQualifications(row, "licenses"), {
            id: "licenses",
            header: t("licenses"),
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.getValue();
                return value?.length ? value.map(renderSettingsResourceLabel).join(", ") : "—";
            },
            filterType: "resource-select",
            filter: renderResourceFilter("/license"),
        }),
        columnHelper.accessor((row) => getQualifications(row, "certifications"), {
            id: "certifications",
            header: t("certifications"),
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.getValue();
                return value?.length ? value.map(renderSettingsResourceLabel).join(", ") : "—";
            },
            filterType: "resource-select",
            filter: renderResourceFilter("/certification"),
        }),
        columnHelper.accessor("data.takeUser", {
            id: "takeUser",
            header: t("taken-by"),
            cell: (info) => {
                const value = info.getValue();
                return value ? renderUserName(value) : "—";
            },
        }),
        columnHelper.accessor("createdAt", {
            id: "createdAt",
            header: t("created-at"),
            cell: (info) => {
                const value = info.getValue();
                return value ? moment(value).format(`${dateFormat} ${timeFormat}`) : "—";
            },
        }),
        columnHelper.display({
            id: "closesIn",
            header: t("closes-in"),
            cell: ({ row }) => {
                const startDate = getStartDate(row.original);
                if (!startDate || !isEditable(row.original)) return "—";
                const closesAt = moment.parseZone(startDate).subtract(coverageDeadline, "hours");
                const minutes = closesAt.diff(moment(), "minutes");
                return (
                    <Badge
                        variant="outline"
                        className={cn(
                            "tw-whitespace-nowrap",
                            minutes <= 0
                                ? STATUS_CLASSNAMES.expired
                                : minutes < 24 * 60
                                    ? STATUS_CLASSNAMES.pending
                                    : "tw-bg-slate-50 tw-text-slate-700 tw-border-slate-200",
                        )}
                        title={closesAt.format(`${dateFormat} ${timeFormat}`)}
                    >
                        {minutes <= 0 ? t("closed") : formatTimeLeft(minutes)}
                    </Badge>
                );
            },
        }),
        columnHelper.accessor("status", {
            header: t("status"),
            enableColumnFilter: true,
            cell: ({ row }) => <StatusCell request={row.original} />,
            filterType: "select",
            filter: (
                <Select showSearch={false}>
                    {Object.keys(STATUS_CLASSNAMES).map((status) => (
                        <Select.Item key={status} value={status}>{t(status)}</Select.Item>
                    ))}
                </Select>
            ),
        }),
        ...(menu === "offers-out" ? [] : [
            columnHelper.display({
                id: "actions",
                enableHiding: false,
                header: () => <div className="tw-text-end tw-pr-4">{t("actions")}</div>,
                cell: ({ row }) => {
                    const request = row.original;
                    const isDirectOffer = request?.type === "direct-offer";
                    const isPending = !isDirectOffer && menu === "pending-approval" && request?.status === "pending";
                    const editable = (isDirectOffer || menu === "needs-coverage") && isEditable(request);
                    const canEditRequest = !isDirectOffer && editable && isCreatedBy(request, user);
                    const canDelete = !isDirectOffer
                        && menu === "needs-coverage"
                        && request?.status === "pending"
                        && isCreatedBy(request, user);
                    const canAssign = !isDirectOffer
                        && (menu === "needs-coverage" || menu === "closing-soon")
                        && isEditable(request);
                    const canCancel = menu === "approved" && request?.status === "approved";

                    return (
                        <div className="tw-flex tw-justify-end">
                            {editable && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    tooltip={canEditRequest
                                        ? t("edit-request")
                                        : t(hasExtraPay(request)
                                            ? "edit-extra-pay"
                                            : "add-extra-pay")
                                    }
                                    onClick={() => edit(request)}
                                >
                                    <Pencil className="tw-h-4 tw-w-4" />
                                </Button>
                            )}
                            {canAssign && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    tooltip={t("assign-to-user")}
                                    onClick={() => assign(request)}
                                >
                                    <UserPlus className="tw-h-4 tw-w-4" />
                                </Button>
                            )}
                            {isPending && (
                                <>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        tooltip={t("approve")}
                                        onClick={() => approve(request)}
                                    >
                                        <CircleCheck className="tw-h-4 tw-w-4 tw-text-green-600" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        tooltip={t("deny")}
                                        onClick={() => deny(request)}
                                    >
                                        <CircleX className="tw-h-4 tw-w-4 tw-text-red-600" />
                                    </Button>
                                </>
                            )}
                            {/* {canCancel && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    tooltip={t("cancel-request")}
                                    onClick={() => cancel(request)}
                                >
                                    <Ban className="tw-h-4 tw-w-4 tw-text-red-600" />
                                </Button>
                            )} */}
                            {canDelete && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    tooltip={t("delete")}
                                    onClick={() => remove(request)}
                                >
                                    <Trash2 className="tw-h-4 tw-w-4 tw-text-red-600" />
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                size="icon"
                                tooltip={t("view")}
                                onClick={() => view(request)}
                            >
                                <Eye className="tw-h-4 tw-w-4" />
                            </Button>
                        </div>
                    );
                },
            }),
        ]),
    ].filter((column) => qualifications[column.id as QualificationField] ?? true),
    [t, dateFormat, timeFormat, currency, user, coverageDeadline, qualifications, approve, deny, edit, view, remove, assign, openProfile, menu]);
};
